import { useEffect, useState, type ReactElement } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useSession } from "@baseline/web/auth";
import { supabase } from "@baseline/web/supabase";
import type { OAuthAuthorizationDetails } from "@supabase/auth-js";

export const Route = createFileRoute("/oauth/consent")({
	component: OAuthConsent,
});

function OAuthConsent(): ReactElement {
	const { data: session, isPending: sessionPending } = useSession();
	const navigate = useNavigate();

	const params = new URLSearchParams(window.location.search);
	const authorizationId = params.get("authorization_id");

	const [details, setDetails] = useState<OAuthAuthorizationDetails | null>(
		null,
	);
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!sessionPending && !session?.user) {
			const returnUrl = window.location.pathname + window.location.search;
			navigate({
				to: "/login",
				search: { redirect: returnUrl },
			});
		}
	}, [sessionPending, session, navigate]);

	useEffect(() => {
		if (!authorizationId || !session?.user) return;

		(async () => {
			const { data, error: err } =
				await supabase.auth.oauth.getAuthorizationDetails(
					authorizationId,
				);

			if (err) {
				setError(err.message);
				setLoading(false);
				return;
			}

			if (data?.redirect_url) {
				window.location.href = data.redirect_url;
				return;
			}

			setDetails(data);
			setLoading(false);
		})();
	}, [authorizationId, session]);

	async function handleAuthorization(
		action: (
			id: string,
			opts: { skipBrowserRedirect: boolean },
		) => Promise<{
			data: { redirect_url?: string } | null;
			error: { message: string } | null;
		}>,
	) {
		if (!authorizationId) return;
		setSubmitting(true);

		const { data, error: err } = await action(authorizationId, {
			skipBrowserRedirect: true,
		});

		if (err) {
			setError(err.message);
			setSubmitting(false);
			return;
		}

		if (data?.redirect_url) {
			window.location.href = data.redirect_url;
		}
	}

	const handleApprove = () =>
		handleAuthorization(
			supabase.auth.oauth.approveAuthorization.bind(supabase.auth.oauth),
		);

	const handleDeny = () =>
		handleAuthorization(
			supabase.auth.oauth.denyAuthorization.bind(supabase.auth.oauth),
		);

	if (sessionPending || loading) {
		return (
			<div className="flex min-h-[60vh] items-center justify-center">
				<div className="text-fg-faint text-sm">Loading...</div>
			</div>
		);
	}

	if (!authorizationId) {
		return (
			<div className="flex min-h-[60vh] items-center justify-center">
				<div className="text-sm text-red-400">
					Missing authorization_id parameter.
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex min-h-[60vh] items-center justify-center">
				<div className="text-sm text-red-400">{error}</div>
			</div>
		);
	}

	const clientName = details?.client?.name ?? "An application";
	const scopes = details?.scope?.split(" ").filter(Boolean) ?? [];

	return (
		<div className="flex min-h-[60vh] items-center justify-center px-6">
			<div className="border-line w-full max-w-md border p-8">
				<h1 className="font-display mb-2 text-xl tracking-tight">
					Authorize access
				</h1>
				<p className="text-fg-tertiary mb-6 text-sm">
					<span className="text-fg-strong font-medium">
						{clientName}
					</span>{" "}
					wants to access your clearvoid account.
				</p>

				{scopes.length > 0 && (
					<div className="mb-6">
						<p className="text-fg-faint mb-2 text-xs font-light tracking-[0.15em] uppercase">
							Requested permissions
						</p>
						<ul className="space-y-1">
							{scopes.map((scope: string) => (
								<li
									key={scope}
									className="text-fg-secondary text-sm"
								>
									{scope}
								</li>
							))}
						</ul>
					</div>
				)}

				<div className="flex gap-3">
					<button
						type="button"
						onClick={handleDeny}
						disabled={submitting}
						className="text-fg-muted hover:text-fg-secondary border-line-emphasis hover:border-fg-faint flex-1 border px-4 py-2.5 text-sm transition-colors disabled:opacity-50"
					>
						Deny
					</button>
					<button
						type="button"
						onClick={handleApprove}
						disabled={submitting}
						className="bg-accent text-surface hover:bg-accent-dim flex-1 px-4 py-2.5 text-sm font-medium transition-colors disabled:opacity-50"
					>
						{submitting ? "Authorizing..." : "Approve"}
					</button>
				</div>
			</div>
		</div>
	);
}
